import { useEffect, useRef, useState } from "react";
import deepEqual from "deep-equal";
import cloneDeep from "lodash/cloneDeep";
import { Location } from "history";
import { nanoid } from "nanoid";
import { FormProps } from "forms/types";
import {
  acknowledgeDraft,
  draftKey,
  FormDraft,
  selectBoundaryValues,
  writeDraft,
} from "utils/formDrafts";
import { setUnsavedChanges } from "utils/localStorage";

// The same immutable snapshot drives the request and its acknowledgement.
export const useFormSaving = (
  props: FormProps,
  values: Record<string, any>,
  baseline: Record<string, any>,
  storageWritable: boolean,
  location: Location,
) => {
  const select = props.persistence
    ? selectBoundaryValues
    : (v: Record<string, any>) => v;
  const saved = useRef(select(baseline));
  const latest = useRef({ props, values, location, select });
  latest.current = { props, values, location, select };
  const writer = useRef(nanoid());
  const revision = useRef(0);
  const draft = useRef<FormDraft>();
  const observed = useRef<unknown>();
  const failed = useRef<unknown>();
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const inFlight = useRef<Promise<void>>();
  const pendingSnapshot = useRef<unknown>();
  const manual = useRef(false);
  const mounted = useRef(true);
  const [completed, setCompleted] = useState(0);
  const key = draftKey(props.persistence?.identity);

  const persist = () => {
    const { props, values, select } = latest.current;
    const selected = select(values);
    if (deepEqual(observed.current, selected)) return;
    observed.current = cloneDeep(selected);
    if (!deepEqual(failed.current, selected)) failed.current = undefined;
    if (!props.persistence?.identity || !storageWritable) return;
    if (deepEqual(saved.current, selected) && !pendingSnapshot.current) {
      acknowledgeDraft(draft.current);
      draft.current = undefined;
      return;
    }
    draft.current = {
      version: 1,
      identity: props.persistence.identity,
      writer: writer.current,
      revision: `${writer.current}:${++revision.current}`,
      updatedAt: Date.now(),
      baseline: cloneDeep(saved.current) as FormDraft["baseline"],
      pending: pendingSnapshot.current
        ? (cloneDeep(pendingSnapshot.current) as FormDraft["values"])
        : undefined,
      values: cloneDeep(selected) as FormDraft["values"],
    };
    writeDraft(draft.current);
  };

  const save = (isManual: boolean): Promise<void> => {
    persist();
    const { props, values, select, location } = latest.current;
    const snapshot = cloneDeep(values);
    const selected = cloneDeep(select(snapshot));
    const stored = draft.current;
    pendingSnapshot.current = selected;
    const request = Promise.resolve().then(async () => {
      try {
        if (isManual) await props.onSubmit(snapshot);
        else await props.onAutosave!(snapshot);
        saved.current = selected;
        failed.current = undefined;
        // A newer local revision or another tab's draft must survive this save.
        if (stored && deepEqual(stored.values, selected))
          acknowledgeDraft(stored);
        pendingSnapshot.current = undefined;
        if (mounted.current) {
          setUnsavedChanges(
            location,
            !deepEqual(latest.current.select(latest.current.values), selected),
          );
          // Rebase edits made during the request onto the acknowledged snapshot.
          observed.current = undefined;
          persist();
        }
      } catch (error) {
        failed.current = selected;
        // A rejected sequence can still have committed its first request.
        // Keep the attempted snapshot as a recovery baseline until a save succeeds.
        if (mounted.current) {
          setUnsavedChanges(location, true);
          observed.current = undefined;
          persist();
          props.onSaveError?.(error);
        }
        throw error;
      } finally {
        inFlight.current = undefined;
        if (mounted.current) setCompleted((n) => n + 1);
      }
    });
    inFlight.current = request;
    return request;
  };
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    persist();
    const selected = latest.current.select(values);
    const dirty =
      !!pendingSnapshot.current || !deepEqual(saved.current, selected);
    if (props.onAutosave) setUnsavedChanges(location, dirty);
    clearTimeout(timer.current);
    if (
      !dirty ||
      !props.onAutosave ||
      inFlight.current ||
      manual.current ||
      deepEqual(failed.current, selected)
    )
      return;
    timer.current = setTimeout(() => {
      void saveRef.current(false).catch(() => {});
    }, 5000);
    return () => clearTimeout(timer.current);
    // Callbacks are read through latest. A rerender must not restart the debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, completed, key, location.pathname]);

  return async () => {
    clearTimeout(timer.current);
    manual.current = true;
    try {
      await inFlight.current?.catch(() => {});
      await saveRef.current(true);
    } catch {
      /* The caller reports the error. Keep the current form and draft. */
    } finally {
      manual.current = false;
      if (mounted.current) setCompleted((n) => n + 1);
    }
  };
};
